import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Chip, Alert, Button, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress,
  Avatar, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, Cancel, Assignment, Refresh } from '@mui/icons-material';
import { AxiosError } from 'axios';
import PartnerDocumentCard from '../partners/PartnerDocumentCard';
import DocumentVerifyConfirmDialog from '../partners/DocumentVerifyConfirmDialog';
import { getPartnerDocuments, verifyPartnerDocument, uploadPartnerDocumentForAdmin, type PartnerRow } from '../../services/partnerUserService';
import { approvePartnerOnboarding, rejectPartnerOnboarding } from '../../services/partnerOnboardingService';
import { formatEmail, formatPhone } from '../../utils/contactMask';
import { useMaskContactSetting } from '../../hooks/useMaskContactSetting';
import { entityLabel, zoneName } from '../../utils/documentReviewUtils';

interface Props {
  partner: PartnerRow | null;
  entityType: string;
  embedded?: boolean;
}

const ONBOARDING_CHIP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  approved: 'success',
  rejected: 'error',
  submitted: 'info',
  documents_pending: 'warning',
  documents_verified: 'info',
  draft: 'default',
};

export default function PartnerReviewDetail({ partner, entityType, embedded = false }: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { maskEnabled } = useMaskContactSetting();

  const [verifyTarget, setVerifyTarget] = useState<{
    docId: string;
    name: string;
    action: 'approve' | 'deny';
  } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [onboardingReject, setOnboardingReject] = useState(false);
  const [onboardingReason, setOnboardingReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const toErrorMessage = (error: unknown) => {
    const axiosErr = error as AxiosError<{ message?: string }>;
    return axiosErr?.response?.data?.message || (error as Error)?.message || 'Request failed. Please try again.';
  };

  const docsQueryKey = [
    'partner-documents',
    partner?._id,
    entityType,
    typeof partner?.zoneId === 'object' ? partner?.zoneId?._id : partner?.zoneId,
    partner?.serviceHub,
  ] as const;

  const { data, isLoading, refetch } = useQuery({
    queryKey: docsQueryKey,
    queryFn: () => getPartnerDocuments(
      partner!._id,
      entityType,
      typeof partner?.zoneId === 'object' ? partner.zoneId._id : partner?.zoneId,
      partner?.serviceHub,
    ),
    enabled: !!partner?._id,
  });

  const invalidateAll = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['partners'] });
    queryClient.invalidateQueries({ queryKey: ['document-queue'] });
    queryClient.invalidateQueries({ queryKey: ['partner-document-review'] });
    queryClient.invalidateQueries({ queryKey: ['partner-onboardings'] });
  };

  const [uploadingConfigId, setUploadingConfigId] = useState<string | null>(null);
  const [verifyingDocId, setVerifyingDocId] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: ({
      file, documentConfigId, extras,
    }: { file: File; documentConfigId: string; extras?: { documentNumber?: string; expiryDate?: string } }) =>
      uploadPartnerDocumentForAdmin(partner!._id, file, documentConfigId, extras),
    onSuccess: () => {
      setActionError(null);
      setUploadingConfigId(null);
      invalidateAll();
    },
    onError: (error) => {
      setUploadingConfigId(null);
      setActionError(toErrorMessage(error));
    },
  });

  type PartnerDocsCache = {
    documents?: Array<{
      config: { _id: string; name?: string };
      uploaded?: { _id?: string; status?: string; rejectionReason?: string; [k: string]: unknown };
      documentStatus: string;
    }>;
    onboarding?: Record<string, unknown>;
    [k: string]: unknown;
  };

  const applyLocalDocStatus = (
    docId: string,
    status: 'approved' | 'rejected',
    reason?: string,
  ) => {
    queryClient.setQueryData<PartnerDocsCache>(docsQueryKey, (old) => {
      if (!old?.documents) return old;
      return {
        ...old,
        documents: old.documents.map((d) => {
          if (String(d.uploaded?._id || '') !== String(docId)) return d;
          return {
            ...d,
            documentStatus: status === 'approved' ? 'APPROVED' : 'DENIED',
            uploaded: {
              ...d.uploaded,
              status,
              rejectionReason: status === 'rejected' ? reason : undefined,
            },
          };
        }),
      };
    });
  };

  const verifyMut = useMutation({
    mutationFn: ({ docId, status, reason }: { docId: string; status: 'approved' | 'rejected'; reason?: string }) =>
      verifyPartnerDocument(partner!._id, docId, status, reason),
    onMutate: async ({ docId, status, reason }) => {
      setVerifyingDocId(docId);
      await queryClient.cancelQueries({ queryKey: docsQueryKey });
      const previous = queryClient.getQueryData<PartnerDocsCache>(docsQueryKey);
      applyLocalDocStatus(docId, status, reason);
      setVerifyTarget(null);
      setRejectReason('');
      setActionError(null);
      return { previous };
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(docsQueryKey, ctx.previous);
      setActionError(toErrorMessage(error));
      setVerifyingDocId(null);
    },
    onSuccess: () => {
      setActionError(null);
      setVerifyingDocId(null);
      // Background reconcile — UI already shows the new status.
      invalidateAll();
    },
  });

  const onboardingApproveMut = useMutation({
    mutationFn: (id: string) => approvePartnerOnboarding(id),
    onSuccess: () => {
      setActionError(null);
      invalidateAll();
    },
    onError: (error) => setActionError(toErrorMessage(error)),
  });

  const onboardingRejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectPartnerOnboarding(id, reason),
    onSuccess: () => {
      setActionError(null);
      setOnboardingReject(false);
      setOnboardingReason('');
      invalidateAll();
    },
    onError: (error) => setActionError(toErrorMessage(error)),
  });

  const onboarding = data?.onboarding as {
    _id?: string;
    status?: string;
    rejectionReason?: string;
    formCode?: string;
    formData?: Record<string, unknown>;
    metadata?: { profileChangesPending?: boolean };
  } | undefined;
  const canActOnOnboarding = onboarding?._id && !['approved', 'rejected'].includes(onboarding.status || '');
  const profileChangesPending = Boolean(onboarding?.metadata?.profileChangesPending);
  const formData = onboarding?.formData || {};
  const profileChangeSummary = [
    formData.porter_vehicle_name || formData.porterVehicleName
      ? `Porter vehicle: ${String(formData.porter_vehicle_name || formData.porterVehicleName)}`
      : formData.porter_vehicle_code || formData.porterVehicleCode
        ? `Porter vehicle: ${String(formData.porter_vehicle_code || formData.porterVehicleCode)}`
        : null,
    formData.vehicle_number || formData.vehicleNumber
      ? `Vehicle no: ${String(formData.vehicle_number || formData.vehicleNumber)}`
      : null,
    formData.vehicle_type || formData.vehicleType
      ? `Vehicle type: ${String(formData.vehicle_type || formData.vehicleType)}`
      : null,
    formData.license_number || formData.licenseNumber
      ? `License: ${String(formData.license_number || formData.licenseNumber)}`
      : null,
    formData.business_name || formData.businessName
      ? `Business: ${String(formData.business_name || formData.businessName)}`
      : null,
    formData.full_name || formData.fullName
      ? `Name: ${String(formData.full_name || formData.fullName)}`
      : null,
  ].filter(Boolean) as string[];
  const STATUS_RANK: Record<string, number> = {
    WAITINGFORAPPROVAL: 0,
    EXPIRED: 1,
    DENIED: 2,
    NOTUPLOADED: 3,
    APPROVED: 4,
  };
  const documents = [...(data?.documents || [])].sort((a, b) => {
    const ar = STATUS_RANK[a.documentStatus] ?? 9;
    const br = STATUS_RANK[b.documentStatus] ?? 9;
    return ar - br;
  });
  const pendingCount = documents.filter((d) => d.documentStatus === 'WAITINGFORAPPROVAL').length;
  const approvedCount = documents.filter((d) => d.documentStatus === 'APPROVED').length;
  const expiredCount = documents.filter((d) => d.documentStatus === 'EXPIRED').length;
  const totalDocs = documents.length;
  const docProgress = totalDocs ? Math.round((approvedCount / totalDocs) * 100) : 0;

  const initials = partner
    ? `${partner.firstName?.[0] || ''}${partner.lastName?.[0] || ''}`.toUpperCase() || '?'
    : '?';

  const handleVerifyConfirm = () => {
    if (!verifyTarget) return;
    verifyMut.mutate({
      docId: verifyTarget.docId,
      status: verifyTarget.action === 'approve' ? 'approved' : 'rejected',
      reason: verifyTarget.action === 'deny' ? rejectReason : undefined,
    });
  };

  if (!partner) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 5, borderRadius: 2.5, textAlign: 'center', minHeight: 420,
          border: 1, borderColor: 'divider', borderStyle: 'dashed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Box>
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
            <Assignment />
          </Avatar>
          <Typography variant="h6" fontWeight={700} gutterBottom>Select a partner</Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={280} mx="auto">
            Choose someone from the review queue to verify documents and complete onboarding approval.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const body = (
    <>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" color="inherit" onClick={() => setActionError(null)}>Dismiss</Button>}>
          {actionError}
        </Alert>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {onboarding && (
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 2.5, borderRadius: 2,
            border: 1,
            borderColor: alpha(theme.palette.primary.main, 0.2),
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
            <Assignment color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>Onboarding</Typography>
            <Chip
              size="small"
              label={(onboarding.status || 'unknown').replace(/_/g, ' ')}
              color={ONBOARDING_CHIP[onboarding.status || ''] || 'default'}
            />
          </Stack>
          {onboarding.formCode && (
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Form: {onboarding.formCode}
            </Typography>
          )}
          {profileChangesPending && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Partner submitted a profile / vehicle update for approval
              {pendingCount === 0 ? ' (no new documents).' : '.'}
            </Alert>
          )}
          {profileChangeSummary.length > 0 && canActOnOnboarding && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Submitted details
              </Typography>
              <Stack spacing={0.25}>
                {profileChangeSummary.map((line) => (
                  <Typography key={line} variant="body2">{line}</Typography>
                ))}
              </Stack>
            </Box>
          )}
          {onboarding.rejectionReason && (
            <Alert severity="error" sx={{ mb: 1 }}>{onboarding.rejectionReason}</Alert>
          )}
          {expiredCount > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {expiredCount} document{expiredCount === 1 ? ' has' : 's have'} expired. Re-upload a valid copy for review.
            </Alert>
          )}
          {canActOnOnboarding && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={onboardingApproveMut.isPending || pendingCount > 0 || expiredCount > 0}
                onClick={() => onboardingApproveMut.mutate(onboarding._id!)}
              >
                {profileChangesPending ? 'Approve changes' : 'Approve onboarding'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setOnboardingReject(true)}
              >
                Reject onboarding
              </Button>
            </Stack>
          )}
          {pendingCount > 0 && canActOnOnboarding && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              Verify all pending documents before approving onboarding.
            </Typography>
          )}
          {onboarding.status === 'approved' && expiredCount === 0 && (
            <Alert severity="success" sx={{ mt: 1 }}>Onboarding approved — partner is active.</Alert>
          )}
        </Paper>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          Documents
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {approvedCount}/{totalDocs} approved
            {expiredCount > 0 ? ` · ${expiredCount} expired` : ''}
          </Typography>
        </Typography>
        <Button size="small" startIcon={<Refresh />} onClick={() => { setActionError(null); refetch(); }}>
          Refresh
        </Button>
      </Stack>
      {totalDocs > 0 && (
        <LinearProgress
          variant="determinate"
          value={docProgress}
          sx={{ height: 6, borderRadius: 99, mb: 2, bgcolor: alpha(theme.palette.success.main, 0.12) }}
          color="success"
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        {documents.map((item: {
          config: { _id: string; name: string; isMandatory?: boolean; identifierRequired?: boolean; expiryRequired?: boolean };
          uploaded?: { _id?: string; fileUrl?: string; documentNumber?: string; expiryDate?: string; rejectionReason?: string };
          documentStatus: string;
        }) => (
          <PartnerDocumentCard
            key={`${item.config._id}-${item.uploaded?._id || 'empty'}`}
            config={item.config}
            uploaded={item.uploaded}
            documentStatus={item.documentStatus as 'NOTUPLOADED' | 'WAITINGFORAPPROVAL' | 'APPROVED' | 'DENIED' | 'EXPIRED'}
            loading={verifyingDocId === item.uploaded?._id && verifyMut.isPending}
            uploading={uploadingConfigId === item.config._id}
            onUpload={(file, extras) => {
              setUploadingConfigId(item.config._id);
              uploadMut.mutate({ file, documentConfigId: item.config._id, extras });
            }}
            onVerifyRequest={(action: 'approved' | 'denied') => {
              if (!item.uploaded?._id) return;
              // Approve immediately — no confirm dialog; status flips in the UI right away.
              if (action === 'approved') {
                verifyMut.mutate({
                  docId: item.uploaded._id,
                  status: 'approved',
                });
                return;
              }
              setVerifyTarget({
                docId: item.uploaded._id,
                name: item.config.name,
                action: 'deny',
              });
              setRejectReason('');
            }}
          />
        ))}
      </Box>

      {!isLoading && !documents.length && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>No document types configured for this partner type and zone.</Alert>
      )}
    </>
  );

  return (
    <>
      {embedded ? body : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            minHeight: 420,
            border: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Avatar sx={{ width: 52, height: 52, fontWeight: 800, bgcolor: 'primary.main' }}>{initials}</Avatar>
              <Box flex={1} minWidth={0}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.1 }}>Partner review</Typography>
                <Typography variant="h6" fontWeight={800} noWrap>
                  {partner.firstName} {partner.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatEmail(partner.email, maskEnabled)} · {formatPhone(partner.phone, maskEnabled)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={entityLabel(entityType)} />
                  {partner.zoneId && (
                    <Chip size="small" variant="outlined" label={zoneName(partner.zoneId)} />
                  )}
                  {pendingCount > 0 && <Chip size="small" color="warning" label={`${pendingCount} pending`} />}
                  {expiredCount > 0 && <Chip size="small" color="error" label={`${expiredCount} expired`} />}
                </Stack>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ p: 2.5, flex: 1, overflow: 'auto' }}>{body}</Box>
        </Paper>
      )}

      <DocumentVerifyConfirmDialog
        open={!!verifyTarget}
        type={verifyTarget?.action || null}
        documentName={verifyTarget?.name}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        loading={verifyMut.isPending}
        onClose={() => { setVerifyTarget(null); setRejectReason(''); }}
        onConfirm={handleVerifyConfirm}
      />

      <Dialog open={onboardingReject} onClose={() => setOnboardingReject(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject onboarding</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={2}
            label="Reason"
            value={onboardingReason}
            onChange={(e) => setOnboardingReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnboardingReject(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!onboardingReason.trim() || onboardingRejectMut.isPending}
            onClick={() => onboarding?._id && onboardingRejectMut.mutate({ id: onboarding._id, reason: onboardingReason })}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
